const customResolvers = new Map();

/**
 * Registers a custom completion resolver for tour steps.
 * Resolver receives the step interaction spec and returns a cleanup function.
 *
 * @param {string} name
 * @param {(spec: import('./types').TourStepInteraction, options: { signal: AbortSignal }) => (() => void) | void} resolver
 */
export function registerCompletionEvent(name, resolver) {
  customResolvers.set(name, resolver);
}

/**
 * @param {import('./types').TourStepInteraction} interaction
 * @param {Object} options
 * @param {Element | null} [options.target]
 * @param {AbortSignal} options.signal
 * @returns {Promise<void>}
 */
export function waitForStepCompletion(interaction, { target, signal }) {
  if (!interaction || interaction.type === "information" || interaction.mode === "next") {
    return Promise.resolve();
  }

  const custom = customResolvers.get(interaction.mode);
  if (custom) {
    return new Promise((resolve) => {
      const cleanup = custom(interaction, { target, signal, resolve });
      const onAbort = () => {
        if (typeof cleanup === "function") {
          cleanup();
        }
        resolve();
      };
      signal.addEventListener("abort", onAbort, { once: true });
    });
  }

  if (interaction.mode === "click_target" || interaction.mode === "button") {
    return waitForTargetEvent(target, "click", signal);
  }

  if (interaction.mode === "longpress_target") {
    return waitForLongPress(target, signal);
  }

  if (interaction.mode === "custom_event" && interaction.eventName) {
    return waitForCustomEvent(interaction.eventName, signal);
  }

  return Promise.resolve();
}

function waitForTargetEvent(target, eventName, signal) {
  return new Promise((resolve) => {
    if (!target) {
      resolve();
      return;
    }

    const handler = (event) => {
      if (!target.contains(event.target)) {
        return;
      }
      cleanup();
      resolve();
    };

    const cleanup = () => {
      target.removeEventListener(eventName, handler);
      signal.removeEventListener("abort", onAbort);
    };

    const onAbort = () => {
      cleanup();
      resolve();
    };

    target.addEventListener(eventName, handler);
    signal.addEventListener("abort", onAbort, { once: true });
  });
}

function waitForLongPress(target, signal) {
  return new Promise((resolve) => {
    if (!target) {
      resolve();
      return;
    }

    let timerId = null;

    const clear = () => {
      if (timerId) {
        window.clearTimeout(timerId);
        timerId = null;
      }
      target.removeEventListener("pointerdown", onPointerDown);
      target.removeEventListener("pointerup", onPointerUp);
      target.removeEventListener("pointercancel", onPointerUp);
      signal.removeEventListener("abort", onAbort);
    };

    const finish = () => {
      clear();
      resolve();
    };

    const onAbort = () => finish();

    const onPointerDown = () => {
      timerId = window.setTimeout(finish, 500);
    };

    const onPointerUp = () => {
      if (timerId) {
        window.clearTimeout(timerId);
        timerId = null;
      }
    };

    target.addEventListener("pointerdown", onPointerDown);
    target.addEventListener("pointerup", onPointerUp);
    target.addEventListener("pointercancel", onPointerUp);
    signal.addEventListener("abort", onAbort, { once: true });
  });
}

function waitForCustomEvent(eventName, signal) {
  return new Promise((resolve) => {
    const handler = () => {
      cleanup();
      resolve();
    };

    const cleanup = () => {
      window.removeEventListener(eventName, handler);
      signal.removeEventListener("abort", onAbort);
    };

    const onAbort = () => {
      cleanup();
      resolve();
    };

    window.addEventListener(eventName, handler);
    signal.addEventListener("abort", onAbort, { once: true });
  });
}
